import { FC, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { isEmpty } from "lodash";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { pl } from "date-fns/locale/pl";
import __ from "src/utils/Translations";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import {
  absencesEmployeeAbsenceRecordsRead,
  absencesOtherEmployeeAbsenceRequestsCreate,
} from "src/utils/RoleBasedSecurity/permissions";
import useBasePath from "src/utils/hooks/useBasePath";
import getYearBoundaries from "src/utils/Company/Absences/getYearBoundaries";
import { useFetchEmployeeAbsences } from "src/Pages/Company/Absences/api/employee.queries";
import useTransformAbsences from "src/utils/hooks/absences/useTransformAbsences";
import YearSwitcher from "src/Pages/Company/Absences/YearSwitcher";
import CalendarPreview from "src/Pages/Company/Absences/Calendars/Calendar/CalendarPreview";
import AbsenceRecordsCalendarLegend from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecordsCalendar/AbsenceRecordsCalendarLegend";
import { Loader } from "src/Components/Loader/Loader";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import {
  IAbsenceRequestDay,
  Status,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";

const AbsenceRecordsCalendar: FC = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const basePath = useBasePath();
  const currentYear = new Date().getFullYear().toString();
  const canCreateAbsenceRequestForEmployee = usePermission(
    absencesOtherEmployeeAbsenceRequestsCreate
  );
  const canReadAbsenceRecords = usePermission(
    absencesEmployeeAbsenceRecordsRead
  );

  const [displayYear, setDisplayYear] = useState(currentYear);
  const [displayMonths, setDisplayMonths] = useState(
    getYearBoundaries(currentYear)
  );

  const [transformAbsences] = useTransformAbsences();

  const {
    data: absencesData,
    isFetching: isFetchingAbsences,
    isError: isErrorAbsences,
  } = useFetchEmployeeAbsences({
    employeeId,
    ...displayMonths,
    statuses: [
      Status.WAITING_FOR_APPROVAL,
      Status.ACCEPTED,
      Status.CORRECTION_REQUIRED,
    ],
  });

  const [absenceRequestDays] = useMemo<[IAbsenceRequestDay[], Date[]]>(() => {
    if (!absencesData) return [[], []];

    return transformAbsences(absencesData);
  }, [absencesData, transformAbsences]);

  const handleYearChange = (year: string) => {
    setDisplayYear(year);
    setDisplayMonths(getYearBoundaries(year));
  };

  const calendars = useMemo(
    () =>
      Array.from(new Array(12)).map((_item, index) => (
        <CalendarPreview
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          year={displayYear}
          month={index}
          absenceRequestDays={absenceRequestDays}
          excludedDays={
            absencesData?.calendarInfos.details[displayYear]?.excludedDays || []
          }
          plannedDays={absencesData?.absencePlans || []}
          disabled={isEmpty(absencesData?.calendarInfos.availableYears)}
        />
      )),
    [absenceRequestDays, absencesData, displayYear]
  );

  if (isErrorAbsences) return <ErrorDataFetching />;

  return (
    <>
      <Paper sx={{ p: "1.5rem" }} data-testid="absence-records-calendar">
        <Typography variant="h6" component="h2">
          {__("Kalendarz roczny")}
        </Typography>
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", mb: "2rem" }}
        >
          <Box sx={{ width: "200px" }}>
            {absencesData?.calendarInfos.availableYears && (
              <YearSwitcher
                years={absencesData?.calendarInfos.availableYears}
                selectedYear={displayYear}
                handleYearChange={handleYearChange}
              />
            )}
          </Box>
          <Box>
            <Button
              variant="contained"
              component={Link}
              size="small"
              to={`${basePath}/absences/absence-request/create/${employeeId}`}
              disabled={!canCreateAbsenceRequestForEmployee}
              sx={{ mr: "0.5rem" }}
              data-testid="new-absence-request-button"
            >
              {__("Złóż wniosek za pracownika")}
            </Button>
            <Button
              variant="contained"
              component={Link}
              size="small"
              to={`${basePath}/absences/calendars-patterns/${employeeId}`}
              disabled={!canReadAbsenceRecords}
              sx={{ ml: "0.5rem" }}
              data-testid="patterns-and-calendars-button"
            >
              {__("Edycja kalendarzy i wzorców")}
            </Button>
          </Box>
        </Stack>
        <Stack
          direction="row"
          sx={{
            flexWrap: "wrap",
            justifyContent: "center",
            position: "relative",
            minHeight: "500px",
          }}
          spacing={4}
          useFlexGap
        >
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
            {isFetchingAbsences ? <Loader /> : calendars}
          </LocalizationProvider>
        </Stack>
        {absencesData && (
          <AbsenceRecordsCalendarLegend
            absenceGroupInfos={absencesData?.absenceGroupInfos}
          />
        )}
      </Paper>
    </>
  );
};

export default AbsenceRecordsCalendar;
